<div class="min-h-screen bg-gray-50">
      <!-- Header -->
      <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <div class="flex justify-between items-center">
          <div>
            <h1 class="text-2xl font-bold text-gray-900">Form Builder</h1>
            <p class="text-gray-600 mt-1">Create and manage your forms</p>
          </div>
          <div class="flex space-x-3">
            <button
              (click)="previewForm()"
              class="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
            >
              Preview
            </button>
            <button
              (click)="saveForm()"
              class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Save Form
            </button>
          </div>
        </div>
      </div>

      <div class="grid grid-cols-12 gap-6">
        <!-- Field Types Palette -->
        <div class="col-span-3">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Field Types</h2>
            <div 
              cdkDropList="field-types"
              [cdkDropListData]="fieldTypes"
              [cdkDropListConnectedTo]="['form-builder']"
              cdkDropListSortingDisabled
              class="space-y-2"
            >
              <div
                *ngFor="let fieldType of fieldTypes"
                cdkDrag
                [cdkDragData]="fieldType"
                class="bg-gray-50 border border-gray-200 rounded-lg p-3 cursor-move hover:bg-gray-100 transition-colors"
              >
                <div class="flex items-center space-x-3">
                  <div class="text-gray-500">
                    <ng-container [ngSwitch]="fieldType.type">
                      <span *ngSwitchCase="'text'">📝</span>
                      <span *ngSwitchCase="'email'">📧</span>
                      <span *ngSwitchCase="'phone'">📞</span>
                      <span *ngSwitchCase="'number'">#</span>
                      <span *ngSwitchCase="'textarea'">📄</span>
                      <span *ngSwitchCase="'about-textarea'">📄</span>
                      <span *ngSwitchCase="'select'">📋</span>
                      <span *ngSwitchCase="'checkbox'">☑️</span>
                      <span *ngSwitchCase="'radio'">🔘</span>
                      <span *ngSwitchCase="'date'">📅</span>
                      <span *ngSwitchCase="'file'">📎</span>
                      <span *ngSwitchCase="'tags'">🏷️</span>
                      <span *ngSwitchCase="'video'">🎥</span>
                    </ng-container>
                  </div>
                  <div>
                    <div class="font-medium text-gray-900">{{ fieldType.label }}</div>
                    <div class="text-sm text-gray-500">{{ fieldType.description }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Form Builder -->
        <div class="col-span-6">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200">
            <!-- Form Header -->
            <div class="pt-6 pl-6 pr-6">
              <h2 class="text-lg font-semibold text-gray-900 mb-4">Form Description</h2>
            </div>
            <div class="pb-6 pl-6 pr-6 border-b border-gray-200">
              <input
                [(ngModel)]="currentForm.description"
                placeholder="Enter Form description"
                class="w-full bg-transparent border-none outline-none focus:ring-0 p-0"
              />
            </div>

            <!-- Form Fields -->
            <div class="p-6">
              <div
                cdkDropList
                id="form-builder"
                [cdkDropListData]="currentForm.fields"
                [cdkDropListConnectedTo]="['field-types']"
                (cdkDropListDropped)="onFieldDrop($event)"
                class="min-h-96 space-y-4"
              >
                <div
                  *ngFor="let field of currentForm.fields; trackBy: trackByFieldId; let i = index"
                  cdkDrag
                  class="bg-gray-50 border-2 border-dashed border-gray-200 rounded-lg p-4 hover:border-blue-300 transition-colors group"
                >
                <div class="flex justify-between items-start mb-3">
                  <div class="flex items-center space-x-2">
                    <span class="text-gray-400">⋮⋮</span>
                    <span class="font-medium text-gray-900">{{ field.label }}</span>
                    <span *ngIf="field.required" class="text-red-500">*</span>
                  </div>
                  <div class="flex space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button
                      (click)="editField(field)"
                      class="text-blue-600 hover:text-blue-800 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      (click)="removeField(i)"
                      class="text-red-600 hover:text-red-800 text-sm"
                    >
                      Remove
                    </button>
                  </div>
                </div>

                <!-- Field Preview -->
                <div [ngSwitch]="field.type">
                  <!-- Text/Email/Phone/Number -->
                  <input
                    *ngSwitchCase="'text'"
                    type="text"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />
                  <input
                    *ngSwitchCase="'email'"
                    type="email"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />
                  <input
                    *ngSwitchCase="'phone'"
                    type="tel"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />
                  <input
                    *ngSwitchCase="'number'"
                    type="number"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />

                  <!-- Textarea -->
                  <textarea
                    *ngSwitchCase="'textarea'"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white resize-none"
                  ></textarea>

                  <!-- Experience -->
                  <textarea
                    *ngSwitchCase="'about-textarea'"
                    [placeholder]="field.placeholder || ''"
                    disabled
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white resize-none"
                  ></textarea>

                  <!-- Select -->
                  <select
                    *ngSwitchCase="'select'"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  >
                    <option>{{ field.placeholder || 'Select an option' }}</option>
                    <option *ngFor="let option of field.options">{{ option }}</option>
                  </select>

                  <!-- Checkboxes -->
                  <div *ngSwitchCase="'checkbox'" class="space-y-2">
                    <label *ngFor="let option of field.options" class="flex items-center space-x-2">
                      <input type="checkbox" disabled class="rounded border-gray-300">
                      <span>{{ option }}</span>
                    </label>
                  </div>

                  <!-- Radio -->
                  <div *ngSwitchCase="'radio'" class="space-y-2">
                    <label *ngFor="let option of field.options" class="flex items-center space-x-2">
                      <input type="radio" [name]="field.id" disabled class="border-gray-300">
                      <span>{{ option }}</span>
                    </label>
                  </div>

                  <!-- Date -->
                  <input
                    *ngSwitchCase="'date'"
                    type="date"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />

                  <!-- File -->
                  <input
                    *ngSwitchCase="'file'"
                    type="file"
                    disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  />

                  <!-- Tags (Preview) -->
                  <div *ngSwitchCase="'tags'">
                    <select disabled
                    class="w-full px-4 py-3 border border-gray-300 rounded-md bg-white"
                  >
                    <option>{{ field.placeholder || '' }}</option>
                    <option *ngFor="let option of field.options">{{ option }}</option>
                  </select>
                  </div>
                </div>
                
                </div>
                <!-- Empty State -->
                <div
                  *ngIf="currentForm.fields.length === 0"
                  class="text-center py-12 text-gray-500"
                >
                  <div class="text-4xl mb-4">📝</div>
                  <h3 class="text-lg font-medium mb-2">Start building your form</h3>
                  <p>Drag and drop fields from the left panel to begin</p>
                </div>
              </div>
            </div>
            
          </div>
        </div>

        <!-- Field Properties Panel -->
        <div class="col-span-3">
          <div class="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <h2 class="text-lg font-semibold text-gray-900 mb-4">Field Properties</h2>
            
            <div *ngIf="selectedField; else noFieldSelected">
              <div class="space-y-4">
                <!-- Label -->
                <div>
                  <label class="block text-sm font-medium text-gray-700 mb-2">Label</label>
                  <input
                    [(ngModel)]="selectedField.label"
                    type="text"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <!-- Placeholder -->
                <div *ngIf="needsPlaceholder(selectedField.type)">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Placeholder</label>
                  <input
                    [(ngModel)]="selectedField.placeholder"
                    type="text"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <!-- Required -->
                <div class="flex items-center">
                  <input
                    [(ngModel)]="selectedField.required"
                    type="checkbox"
                    id="required"
                    class="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <label for="required" class="ml-2 text-sm text-gray-700">Required field</label>
                </div>

                <!-- Options for select/checkbox/radio/tags -->
                <div *ngIf="needsOptions(selectedField.type)">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Options</label>
                  <div class="space-y-2">
                    <div
                      *ngFor="let option of selectedField.options; let i = index; trackBy: trackByIndex"
                      class="flex items-center space-x-2"
                    >
                      <input
                        [(ngModel)]="selectedField.options![i]"
                        type="text"
                        class="flex-1 px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                      
                      <!-- Required for Interview Checkbox and Info Icon -->
                      <div class="flex items-center space-x-1">
                        <div class="relative group flex">
                          <input
                            [checked]="isOptionRequiredForInterview(selectedField.options![i])"
                            (change)="toggleRequiredForInterview(i, $event)"
                            type="checkbox"
                            [id]="'required-for-interview-' + i"
                            class="rounded border-gray-300 text-blue-600 focus:ring-blue-500 mr-2"
                          />
                          <!-- Tooltip -->
                          <div class="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap z-10">
                            Required for scheduling an interview
                            <div class="absolute top-full left-1/2 transform -translate-x-1/2 border-4 border-transparent border-t-gray-800"></div>
                          </div>
                        </div>
                        
                        <!-- Remove Button -->
                        <button
                          (click)="removeOption(i)"
                          class="text-red-600 hover:text-red-800 ml-1"
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                    <button
                      (click)="addOption()"
                      class="w-full px-4 py-3 border border-gray-300 rounded-md text-gray-600 hover:bg-gray-50"
                    >
                      + Add Option
                    </button>
                  </div>
                </div>

                <!-- Validation -->
                <div *ngIf="needsValidation(selectedField.type)">
                  <label class="block text-sm font-medium text-gray-700 mb-2">Validation</label>
                  <div class="space-y-3">
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">Min Length</label>
                      <input
                        [(ngModel)]="selectedField.validation!.minLength"
                        type="number"
                        min="0"
                        class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">Max Length</label>
                      <input
                        [(ngModel)]="selectedField.validation!.maxLength"
                        type="number"
                        min="1"
                        class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                    <div>
                      <label class="block text-xs text-gray-600 mb-1">Pattern (RegEx)</label>
                      <input
                        [(ngModel)]="selectedField.validation!.pattern"
                        type="text"
                        class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <ng-template #noFieldSelected>
              <div class="text-center py-8 text-gray-500">
                <div class="text-3xl mb-3">⚙️</div>
                <p>Select a field to edit its properties</p>
              </div>
            </ng-template>
          </div>
        </div>
      </div>

      <!-- Preview Modal -->
      <div
        *ngIf="showPreview"
        class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
        (click)="closePreview()"
      >
        <div
          class="bg-white rounded-lg shadow-xl w-[80%] w-full mx-4 max-h-screen overflow-y-auto"
          (click)="$event.stopPropagation()"
        >
          <div class="p-6 border-b border-gray-200">
            <div class="flex justify-between items-center">
              <h2 class="text-xl font-semibold">Form Preview</h2>
              <button
                (click)="closePreview()"
                class="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>
          </div>
          <div class="p-6">
            <form class="space-y-4">
              <div>
                <p class="text-gray-600 mt-1">{{ currentForm.description }}</p>
              </div>

              <div
                *ngFor="let field of currentForm.fields; let i = index"
                class="space-y-2"
              >
                <label class="block text-sm font-medium text-gray-700" *ngIf="field.label != 'Video'">
                  {{ field.label }}
                  <span *ngIf="field.required" class="text-red-500">*</span>
                </label>

                <div [ngSwitch]="field.type">
                  <input
                    *ngSwitchCase="'text'"
                    type="text"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    *ngSwitchCase="'email'"
                    type="email"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    *ngSwitchCase="'phone'"
                    type="tel"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    *ngSwitchCase="'number'"
                    type="number"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <textarea
                    *ngSwitchCase="'textarea'"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                  <textarea
                    *ngSwitchCase="'about-textarea'"
                    [placeholder]="field.placeholder || ''"
                    [required]="field.required"
                    rows="3"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                  <select
                    *ngSwitchCase="'select'"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">{{ field.placeholder || 'Select an option' }}</option>
                    <option *ngFor="let option of field.options" [value]="option">{{ option }}</option>
                  </select>
                  <div *ngSwitchCase="'checkbox'" class="space-y-2">
                    <label *ngFor="let option of field.options" class="flex items-center space-x-2">
                      <input type="checkbox" class="rounded border-gray-300 text-blue-600 focus:ring-blue-500">
                      <span>{{ option }}</span>
                    </label>
                  </div>
                  <div *ngSwitchCase="'radio'" class="space-y-2">
                    <div class="grid grid-cols-2 gap-4">
                      <button *ngFor="let option of field.options"
                              type="button"
                              (click)="setRadioButtonValue(i, option)"
                              class="flex items-center justify-center px-6 py-4 border rounded-lg transition-colors"
                              [ngClass]="[
                                getRadioValue(i) === option.toLowerCase() && option.toLowerCase() === 'no'
                                  ? 'border-red-300 bg-red-50'
                                  : getRadioValue(i) === option.toLowerCase()
                                    ? 'border-green-300 bg-green-50'
                                    : 'border-gray-300 hover:border-blue-300'
                              ]">
                        
                        <!-- Checkmark only if this option is selected -->
                        <svg *ngIf="getRadioValue(i) === option.toLowerCase()"
                            class="w-5 h-5 mr-2" [ngClass]="[
                                getRadioValue(i) === option.toLowerCase() && option.toLowerCase() === 'no'
                                  ? 'text-red-600'
                                  : getRadioValue(i) === option.toLowerCase()
                                    ? 'text-green-600'
                                    : 'text-red-600'
                              ]"
                            fill="currentColor" viewBox="0 0 20 20">
                          <path fill-rule="evenodd"
                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                clip-rule="evenodd"></path>
                        </svg>

                        <span>{{ option }}</span>
                      </button>
                    </div>
                  </div>
                  <input
                    *ngSwitchCase="'date'"
                    type="date"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    *ngSwitchCase="'file'"
                    type="file"
                    [required]="field.required"
                    class="w-full px-4 py-3 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                  />
                  
                  <!-- Tags Field -->
                  <div *ngSwitchCase="'tags'">
                    <div class="w-full">
                      <!-- Selected count -->
                      <div class="mb-8">
                        <div class="mt-4 flex items-center gap-2">
                          <div class="flex gap-1">
                            <div class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 scale-100" [ngClass]="selectedTags['undefined']?.length && selectedTags['undefined'].length >= 1 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'">
                              1
                            </div>
                            <div class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 scale-100" [ngClass]="selectedTags['undefined']?.length && selectedTags['undefined'].length >= 2 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'">
                              2
                            </div>
                            <div class="w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all duration-300 scale-100" [ngClass]="selectedTags['undefined']?.length && selectedTags['undefined'].length >= 3 ? 'bg-blue-600 text-white' : 'bg-slate-200 text-slate-400'">
                              2
                            </div>
                          </div>
                          <span class="text-slate-500 text-sm ml-2">
                            0/3 selected
                          </span>
                        </div>
                      </div>

                      <!-- Buttons grid -->
                      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                        <button
                          *ngFor="let option of field.options"
                          type="button"
                          class="relative flex justify-between px-5 py-4 rounded-xl border-2 transition-all duration-200"
                          [ngClass]="{
                            'border-blue-600 bg-blue-50 text-blue-900': selectedTags[field.id]?.includes(option),
                            'bg-white text-gray-700 border-gray-300': !selectedTags[field.id]?.includes(option)
                          }"
                          (click)="toggleTag(field.id, option)"
                        >
                          <span class="font-medium">{{ option }}</span>
                          <div
                            *ngIf="selectedTags[field.id]?.includes(option)"
                            class="w-6 h-6 bg-blue-600 rounded-full flex items-center justify-center animate-in zoom-in duration-200"
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="3"
                              stroke-linecap="round"
                              stroke-linejoin="round"
                              class="lucide lucide-check w-4 h-4 text-white"
                            >
                              <path d="M20 6 9 17l-5-5"></path>
                            </svg>
                          </div>
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div *ngSwitchCase="'video'">
                    <app-video></app-video>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>